import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiMenu, FiX } from 'react-icons/fi';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const isHome = location.pathname === '/';

  const styles = {
    navbar: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      padding: '16px 0',
      transition: 'all 0.3s ease',
      background: scrolled || !isHome ? 'white' : 'transparent',
      boxShadow: scrolled ? '0 2px 8px rgba(0,0,0,0.08)' : 'none'
    },
    container: {
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '0 24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      textDecoration: 'none',
      fontWeight: '700',
      fontSize: '1.5rem',
      color: scrolled || !isHome ? '#1a3a5c' : 'white'
    },
    logoIcon: {
      width: '36px',
      height: '36px',
      background: scrolled || !isHome ? '#1a3a5c' : '#d4a5a5',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: scrolled || !isHome ? 'white' : '#1a3a5c',
      fontSize: '1.25rem'
    },
    nav: {
      display: 'flex',
      alignItems: 'center',
      gap: '32px'
    },
    navLinks: {
      display: 'flex',
      alignItems: 'center',
      gap: '32px',
      listStyle: 'none',
      margin: 0,
      padding: 0
    },
    navLink: {
      color: scrolled || !isHome ? '#222' : 'white',
      textDecoration: 'none',
      fontWeight: '500',
      fontSize: '0.9375rem',
      transition: 'color 0.2s ease'
    },
    mobileToggle: {
      display: 'none',
      background: 'none',
      border: 'none',
      fontSize: '1.5rem',
      cursor: 'pointer',
      color: scrolled || !isHome ? '#222' : 'white'
    },
    mobileMenu: {
      position: 'fixed',
      top: 0,
      right: 0,
      bottom: 0,
      width: '300px',
      background: 'white',
      boxShadow: '-4px 0 20px rgba(0,0,0,0.1)',
      padding: '24px',
      transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
      transition: 'transform 0.3s ease',
      zIndex: 1001
    },
    mobileOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      opacity: isOpen ? 1 : 0,
      visibility: isOpen ? 'visible' : 'hidden',
      transition: 'opacity 0.3s ease',
      zIndex: 1000
    },
    mobileHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '32px'
    },
    mobileNavLinks: {
      listStyle: 'none',
      padding: 0,
      margin: 0
    },
    mobileNavLink: {
      display: 'block',
      padding: '16px 0',
      borderBottom: '1px solid #eee',
      color: '#222',
      textDecoration: 'none',
      fontWeight: '500'
    }
  };

  // Mobile styles
  const mobileBreakpoint = 768;
  const [isMobile, setIsMobile] = useState(window.innerWidth < mobileBreakpoint);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < mobileBreakpoint);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <nav style={styles.navbar}>
        <div style={styles.container}>
          <Link to="/" style={styles.logo}>
            <span style={styles.logoIcon}>K</span>
            Kasbia Stays
          </Link>

          {!isMobile ? (
            <div style={styles.nav}>
              <ul style={styles.navLinks}>
                <li><Link to="/" style={styles.navLink}>Home</Link></li>
                <li><Link to="/apartments" style={styles.navLink}>Apartments</Link></li>
                <li><Link to="/locations" style={styles.navLink}>Locations</Link></li>
                <li><Link to="/about" style={styles.navLink}>About</Link></li>
                <li><Link to="/contact" style={styles.navLink}>Contact</Link></li>
              </ul>
            </div>
          ) : (
            <button style={{...styles.mobileToggle, display: 'block'}} onClick={() => setIsOpen(true)}>
              <FiMenu />
            </button>
          )}
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div style={styles.mobileOverlay} onClick={() => setIsOpen(false)} />

      {/* Mobile Menu */}
      <div style={styles.mobileMenu}>
        <div style={styles.mobileHeader}>
          <Link to="/" style={{...styles.logo, color: '#1a3a5c'}}>
            <span style={{...styles.logoIcon, background: '#1a3a5c', color: 'white'}}>K</span>
            Kasbia Stays
          </Link>
          <button style={{...styles.mobileToggle, display: 'block', color: '#222'}} onClick={() => setIsOpen(false)}>
            <FiX />
          </button>
        </div>

        <ul style={styles.mobileNavLinks}>
          <li><Link to="/" style={styles.mobileNavLink}>Home</Link></li>
          <li><Link to="/apartments" style={styles.mobileNavLink}>Apartments</Link></li>
          <li><Link to="/locations" style={styles.mobileNavLink}>Locations</Link></li>
          <li><Link to="/about" style={styles.mobileNavLink}>About</Link></li>
          <li><Link to="/contact" style={styles.mobileNavLink}>Contact</Link></li>
          <li><Link to="/faq" style={styles.mobileNavLink}>FAQ</Link></li>
        </ul>
      </div>

      {/* Spacer for fixed navbar */}
      <div style={{ height: isHome ? 0 : '80px' }} />
    </>
  );
};

export default Navbar;
